import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { LoadPyodide } from '@/types/global';
import alert from '@/utils/shared/alert';
import { useTranslation } from 'react-i18next';

type PyodideContextType = {
  pyodide: LoadPyodide | null;
  loading: boolean;
  error: string | null;
  loadPyodideInstance: () => Promise<void>;
};

const PyodideContext = createContext<PyodideContextType>(
  {} as PyodideContextType,
);

export const usePyodide = () => useContext(PyodideContext);

interface PyodideProviderProps {
  children: ReactNode;
}

export const PyodideProvider = ({ children }: PyodideProviderProps) => {
  const [pyodide, setPyodide] = useState<LoadPyodide | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [t] = useTranslation('translation', {
    keyPrefix: 'codeEditor',
  });

  const loadPyodideInstance = useCallback(async () => {
    if (pyodide || loading) return;

    try {
      setLoading(true);
      setError(null);
      const pyodideInstance = await window.loadPyodide();

      setPyodide(pyodideInstance);
    } catch {
      alert.warning(t('notStarted'));
      alert.error('Erro ao carregar Pyodide');
    } finally {
      setLoading(false);
    }
  }, [pyodide, loading, t]);

  return (
    <PyodideContext.Provider
      value={{ pyodide, loading, error, loadPyodideInstance }}
    >
      {children}
    </PyodideContext.Provider>
  );
};
